import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { listAdminFilterPresets } from './api';
import { decodeFilters, emptyFilters, writeFiltersToSearchParams } from './filterSerialization';
import { useAdminFilterMetadata } from './useAdminFilterMetadata';
import { useAdminSearch } from './useAdminSearch';
import type { AdminFilterResource, DynamicFilterCondition, DynamicFilterGroup } from './types';

export function useDynamicAdminList<T>(resource: AdminFilterResource, options?: {
  initialSortField?: string;
  initialSortDir?: 'asc' | 'desc';
  pageSize?: number;
  onFiltersChanged?: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [size] = useState(options?.pageSize ?? 20);
  const [sortField, setSortField] = useState(options?.initialSortField ?? 'id');
  const [sortDir, setSortDir] = useState(options?.initialSortDir ?? 'desc');
  const [filters, setFilters] = useState<DynamicFilterGroup>(() => decodeFilters(searchParams.get('filters')));
  const defaultPresetAppliedRef = useRef(false);

  const metadataQuery = useAdminFilterMetadata(resource);
  const presetQuery = useQuery({
    queryKey: ['admin-filter-presets', resource],
    queryFn: () => listAdminFilterPresets(resource),
    staleTime: 30 * 1000,
  });
  const searchableFilters = useMemo(() => compactFilters(filters), [filters]);
  const searchRequest = useMemo(() => ({
    page,
    size,
    sort: [{ field: sortField, direction: sortDir.toUpperCase() as 'ASC' | 'DESC' }],
    filters: searchableFilters,
  }), [page, size, sortField, sortDir, searchableFilters]);
  const searchQuery = useAdminSearch<T>(resource, searchRequest);

  const handleSort = useCallback((field: string) => {
    const sortable = metadataQuery.data?.fields?.some((item) => item.key === field && item.sortable);
    if (metadataQuery.data && !sortable) return;
    setSortDir((prev) => (sortField === field && prev === 'asc' ? 'desc' : 'asc'));
    setSortField(field);
    setPage(0);
  }, [metadataQuery.data, sortField]);

  const updateFilters = useCallback((next: DynamicFilterGroup) => {
    setFilters(next);
    setSearchParams((current) => writeFiltersToSearchParams(current, next), { replace: true });
    setPage(0);
    options?.onFiltersChanged?.();
  }, [options, setSearchParams]);

  const resetFilters = useCallback(() => {
    updateFilters(emptyFilters());
  }, [updateFilters]);

  useEffect(() => {
    if (defaultPresetAppliedRef.current) return;
    if (searchParams.has('filters')) {
      defaultPresetAppliedRef.current = true;
      return;
    }
    if (!presetQuery.data) return;
    defaultPresetAppliedRef.current = true;
    const defaultPreset = presetQuery.data.find((preset) => preset.isDefault);
    if (defaultPreset) {
      updateFilters(defaultPreset.filters);
    }
  }, [presetQuery.data, searchParams, updateFilters]);

  return {
    resource,
    page,
    size,
    setPage,
    sortField,
    sortDir,
    handleSort,
    filters,
    setFilters: updateFilters,
    resetFilters,
    metadataQuery,
    presetQuery,
    searchQuery,
  };
}

function compactFilters(filters: DynamicFilterGroup): DynamicFilterGroup {
  return {
    logic: 'AND',
    conditions: filters.conditions.filter(isCompleteCondition),
  };
}

function isCompleteCondition(condition: DynamicFilterCondition) {
  if (!condition.field || !condition.operator) return false;
  const value = condition.value;
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') {
    const range = value as { from?: unknown; to?: unknown };
    return range.from !== undefined && range.from !== '' && range.to !== undefined && range.to !== '';
  }
  if (typeof value === 'boolean') return true;
  return value !== undefined && value !== null && value !== '';
}
